/**
 * Resource API Service
 *
 * Handles all API calls for workshop resources:
 * - Loan Cars
 * - Equipment
 * - Tools
 * - Spaces
 * - Consultants
 */

import axiosInstance from './axiosInstance';

// ============================================
// Loan Cars
// ============================================

export const getLoanCarsApi = async () => {
    const response = await axiosInstance.get('/resources/loan-cars');
    return response.data;
};

export const getLoanCarsPaginatedApi = async ({ page = 0, size = 10 } = {}) => {
    const response = await axiosInstance.get('/resources/loan-cars/paginated', {
        params: { page, size }
    });
    return response.data;
};

export const getLoanCarApi = async (uuid) => {
    const response = await axiosInstance.get(`/resources/loan-cars/${uuid}`);
    return response.data;
};

export const createLoanCarApi = async (data) => {
    const response = await axiosInstance.post('/resources/loan-cars', data);
    return response.data;
};

export const updateLoanCarApi = async (uuid, data) => {
    const response = await axiosInstance.put(`/resources/loan-cars/${uuid}`, data);
    return response.data;
};

export const deleteLoanCarApi = async (uuid) => {
    const response = await axiosInstance.delete(`/resources/loan-cars/${uuid}`);
    return response.data;
};

/**
 * Fetch all allocations for a specific loan car (used for the Gantt schedule view).
 * Returns LoanCarAllocation list with embedded booking and customer details.
 */
export const getLoanCarAllocationsApi = async (uuid) => {
    const response = await axiosInstance.get(`/resources/loan-cars/${uuid}/allocations`);
    return response.data;
};

/**
 * Fetch paginated incident history for a loan car rego plate.
 * Results are newest-first, 20 incidents per page.
 *
 * @param {string} rego  - registration plate
 * @param {number} page  - zero-based page index (default 0)
 * @returns {{ content, page, totalPages, totalElements }}
 */
export const getLoanCarIncidentHistoryApi = async (rego, page = 0) => {
    const response = await axiosInstance.get(
        `/loan-car-incidents/rego/${encodeURIComponent(rego)}/history`,
        { params: { page } }
    );
    return response.data;
};

/**
 * Batch-check the current incident status for a list of rego plates.
 * Returns [{ rego, activeIncident }] — activeIncident=true means the car
 * has an open OUT with no paired IN (currently with a customer).
 *
 * @param {string[]} regos - array of registration plates
 */
export const getLoanCarRegoStatusApi = async (regos = []) => {
    if (!regos.length) return [];
    const response = await axiosInstance.get('/loan-car-incidents/rego-status', {
        params: { regos: regos.join(',') }
    });
    return response.data; // [{ rego, activeIncident }]
};

// ============================================
// Equipment
// ============================================

export const getEquipmentListApi = async () => {
    const response = await axiosInstance.get('/resources/equipment');
    return response.data;
};

export const getEquipmentPaginatedApi = async ({ page = 0, size = 10 } = {}) => {
    const response = await axiosInstance.get('/resources/equipment/paginated', {
        params: { page, size }
    });
    return response.data;
};

export const getEquipmentApi = async (uuid) => {
    const response = await axiosInstance.get(`/resources/equipment/${uuid}`);
    return response.data;
};

export const createEquipmentApi = async (data) => {
    const response = await axiosInstance.post('/resources/equipment', data);
    return response.data;
};

export const updateEquipmentApi = async (uuid, data) => {
    const response = await axiosInstance.put(`/resources/equipment/${uuid}`, data);
    return response.data;
};

export const deleteEquipmentApi = async (uuid) => {
    const response = await axiosInstance.delete(`/resources/equipment/${uuid}`);
    return response.data;
};

// ============================================
// Tools
// ============================================

export const getToolsApi = async () => {
    const response = await axiosInstance.get('/resources/tools');
    return response.data;
};

export const getToolsPaginatedApi = async ({ page = 0, size = 10 } = {}) => {
    const response = await axiosInstance.get('/resources/tools/paginated', {
        params: { page, size }
    });
    return response.data;
};

export const getToolApi = async (uuid) => {
    const response = await axiosInstance.get(`/resources/tools/${uuid}`);
    return response.data;
};

export const createToolApi = async (data) => {
    const response = await axiosInstance.post('/resources/tools', data);
    return response.data;
};

export const updateToolApi = async (uuid, data) => {
    const response = await axiosInstance.put(`/resources/tools/${uuid}`, data);
    return response.data;
};

export const deleteToolApi = async (uuid) => {
    const response = await axiosInstance.delete(`/resources/tools/${uuid}`);
    return response.data;
};

// ============================================
// Spaces
// ============================================

export const getSpacesApi = async () => {
    const response = await axiosInstance.get('/resources/spaces');
    return response.data;
};

export const getSpacesPaginatedApi = async ({ page = 0, size = 10 } = {}) => {
    const response = await axiosInstance.get('/resources/spaces/paginated', {
        params: { page, size }
    });
    return response.data;
};

export const getSpaceApi = async (uuid) => {
    const response = await axiosInstance.get(`/resources/spaces/${uuid}`);
    return response.data;
};

export const createSpaceApi = async (data) => {
    const response = await axiosInstance.post('/resources/spaces', data);
    return response.data;
};

export const updateSpaceApi = async (uuid, data) => {
    const response = await axiosInstance.put(`/resources/spaces/${uuid}`, data);
    return response.data;
};

export const deleteSpaceApi = async (uuid) => {
    const response = await axiosInstance.delete(`/resources/spaces/${uuid}`);
    return response.data;
};

// ============================================
// Consultants
// ============================================

export const getConsultantsApi = async () => {
    const response = await axiosInstance.get('/resources/consultants');
    return response.data;
};

export const getConsultantsPaginatedApi = async ({ page = 0, size = 10 } = {}) => {
    const response = await axiosInstance.get('/resources/consultants/paginated', {
        params: { page, size }
    });
    return response.data;
};

export const getConsultantApi = async (uuid) => {
    const response = await axiosInstance.get(`/resources/consultants/${uuid}`);
    return response.data;
};

export const createConsultantApi = async (data) => {
    const response = await axiosInstance.post('/resources/consultants', data);
    return response.data;
};

export const updateConsultantApi = async (uuid, data) => {
    const response = await axiosInstance.put(`/resources/consultants/${uuid}`, data);
    return response.data;
};

export const deleteConsultantApi = async (uuid) => {
    const response = await axiosInstance.delete(`/resources/consultants/${uuid}`);
    return response.data;
};

// ============================================
// Job Allocations (resource planning view)
// ============================================

/**
 * Get all active job allocations for a resource.
 * Returns ResourceJobAllocationResponse list with job number, status, title.
 */
export const getResourceJobAllocationsApi = async (resourceUuid) => {
    const response = await axiosInstance.get(`/resources/${resourceUuid}/job-allocations`);
    return response.data;
};

/**
 * Unassign this resource from a specific job.
 * Resets resource status to AVAILABLE if no other allocations remain.
 */
export const unassignResourceFromJobApi = async (resourceUuid, jobUuid) => {
    const response = await axiosInstance.delete(`/resources/${resourceUuid}/job-allocations/${jobUuid}`);
    return response.data;
};

// ============================================
// Common Operations
// ============================================

export const updateResourceStatusApi = async (uuid, status) => {
    const response = await axiosInstance.patch(`/resources/${uuid}/status`, { status });
    return response.data;
};

export const getResourceSummaryApi = async () => {
    const response = await axiosInstance.get('/resources/summary');
    return response.data;
};


// ============================================
// Default Export
// ============================================

const resourceApi = {
    // Loan Cars
    getLoanCarsApi,
    getLoanCarsPaginatedApi,
    getLoanCarApi,
    createLoanCarApi,
    updateLoanCarApi,
    deleteLoanCarApi,
    getLoanCarAllocationsApi,
    getLoanCarIncidentHistoryApi,
    getLoanCarRegoStatusApi,
    // Equipment
    getEquipmentListApi,
    getEquipmentPaginatedApi,
    getEquipmentApi,
    createEquipmentApi,
    updateEquipmentApi,
    deleteEquipmentApi,
    // Tools
    getToolsApi,
    getToolsPaginatedApi,
    getToolApi,
    createToolApi,
    updateToolApi,
    deleteToolApi,
    // Spaces
    getSpacesApi,
    getSpacesPaginatedApi,
    getSpaceApi,
    createSpaceApi,
    updateSpaceApi,
    deleteSpaceApi,
    // Consultants
    getConsultantsApi,
    getConsultantsPaginatedApi,
    getConsultantApi,
    createConsultantApi,
    updateConsultantApi,
    deleteConsultantApi,
    // Job Allocations (resource planning)
    getResourceJobAllocationsApi,
    unassignResourceFromJobApi,
    // Common
    updateResourceStatusApi,
    getResourceSummaryApi,
};

export default resourceApi;